import React, { useCallback, useEffect, useState } from "react";
import Admin from "../layout/Admin";
import { getPrinters, getInk } from "../../actions/printer";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Spinner from "../layout/Spinner";
import PrinterItem from "./PrinterItem";
import EditPrinter from "./EditPrinter";
import { HEALTH, healthOf } from "../../utils/printerHealth";

const REFRESH_MS = 60 * 1000;
// Ink lasts days, and its estimate is heavier than the printers
const INK_REFRESH_MS = 10 * 60 * 1000;
// The marks of a tank until the backend says, as utils/service.js has them
const DEFAULT_MARKS = 5;

// The list in sections, the one to act on first, each with the states it holds
const SECTIONS = [
  { title: "Needs attention", states: ["offline", "error", "printerProblem"] },
  { title: "Low paper or ink", states: ["lowPaper", "lowInk"] },
  { title: "Working", states: ["ok"] },
  { title: "Inactive", states: ["never", "disabled"] },
];

// Filter chips, worst first. These two only appear while some printer is in them.
const FILTERS = SECTIONS.flatMap(({ states }) => states);
const HIDDEN_WHEN_EMPTY = ["never", "disabled"];

const chipClass = (active, empty) =>
  "flex items-center flex-shrink-0 px-3 py-2 text-sm font-medium leading-5 border rounded-full focus:outline-none " +
  (active
    ? "text-white bg-purple-600 border-purple-600"
    : "text-gray-700 bg-white border-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700") +
  (empty ? " opacity-50 cursor-default" : "");

const Printer = ({ getPrinters, getInk, printers, loading }) => {
  useEffect(() => {
    getPrinters();
    // Keep the connection states current while the page stays open.
    const refresh = setInterval(getPrinters, REFRESH_MS);
    return () => clearInterval(refresh);
  }, [getPrinters]);
  // The ink each kiosk has left, by printer id, and the marks of a tank. The edit
  // sheet loads it again once it has recorded ink.
  const [ink, setInk] = useState(new Map());
  const [marks, setMarks] = useState(DEFAULT_MARKS);
  const loadInk = useCallback(
    () =>
      getInk().then((data) => {
        if (!data) return;
        setInk(new Map(data.printers.map((item) => [item.id, item])));
        setMarks(data.marks);
      }),
    [getInk]
  );
  useEffect(() => {
    loadInk();
    const refresh = setInterval(loadInk, INK_REFRESH_MS);
    return () => clearInterval(refresh);
  }, [loadInk]);
  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("all");
  const openPrinter = (printer) => {
    setSelectedId(printer._id);
    setShowModal(true);
  };
  // Read the printer from the store, so the open sheet follows the refresh and its own edits.
  const selected = printers.find((printer) => printer._id === selectedId);

  const items = printers
    .map((printer) => ({ printer, health: healthOf(printer, ink.get(printer._id)) }))
    .sort(
      (a, b) =>
        HEALTH[a.health].rank - HEALTH[b.health].rank ||
        String(a.printer.printerName).localeCompare(
          String(b.printer.printerName),
          undefined,
          { numeric: true }
        )
    );
  const counts = {};
  items.forEach(({ health }) => {
    counts[health] = (counts[health] || 0) + 1;
  });
  const shown =
    filter === "all" ? items : items.filter(({ health }) => health === filter);

  return (
    <>
      {!loading ? (
        <Admin>
          <h2 className="my-6 text-2xl font-semibold text-gray-700 dark:text-gray-200">
            Принтер
          </h2>

          {printers.length !== 0 && (
            <>
              <div className="sticky top-0 z-10 min-w-0 px-6 py-3 -mx-6 bg-gray-50 dark:bg-gray-900">
                <div className="flex gap-2 overflow-x-auto sm:flex-wrap">
                  <button
                    className={chipClass(filter === "all", false)}
                    onClick={() => setFilter("all")}
                  >
                    All
                    <span className="ml-2 font-semibold">{items.length}</span>
                  </button>
                  {FILTERS.filter(
                    (key) => counts[key] || !HIDDEN_WHEN_EMPTY.includes(key)
                  ).map((key) => (
                    <button
                      key={key}
                      className={chipClass(filter === key, !counts[key])}
                      disabled={!counts[key] && filter !== key}
                      onClick={() => setFilter(filter === key ? "all" : key)}
                    >
                      <span
                        className={
                          "inline-block w-2 h-2 mr-2 rounded-full " +
                          HEALTH[key].dot
                        }
                      ></span>
                      {HEALTH[key].label}
                      <span className="ml-2 font-semibold">
                        {counts[key] || 0}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {SECTIONS.map(({ title, states }) => {
                const cards = shown.filter(({ health }) =>
                  states.includes(health)
                );
                if (cards.length === 0) return null;
                return (
                  <section key={title} className="mt-2 mb-8">
                    {/* The chip of a filter already names what is listed */}
                    {filter === "all" && (
                      <h3 className="mb-3 text-sm font-semibold text-gray-600 dark:text-gray-300">
                        {title}
                        <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
                          {cards.length}
                        </span>
                      </h3>
                    )}
                    {/* grid-cols-1 and min-w-0 keep a long, truncated error from widening the card past a phone screen */}
                    <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                      {cards.map(({ printer, health }) => (
                        <PrinterItem
                          printer={printer}
                          health={health}
                          key={printer._id}
                          openPrinter={openPrinter}
                          ink={ink.get(printer._id)}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
              {shown.length === 0 && (
                <p className="mb-8 text-sm text-gray-600 dark:text-gray-400">
                  No printers in this state.
                </p>
              )}

              {showModal && selected ? (
                <EditPrinter
                  printer={selected}
                  showModal={showModal}
                  setShowModal={setShowModal}
                  ink={ink.get(selected._id)}
                  marks={marks}
                  onInkSaved={loadInk}
                />
              ) : (
                ""
              )}
            </>
          )}
        </Admin>
      ) : (
        <Spinner />
      )}
    </>
  );
};

Printer.propTypes = {
  getPrinters: PropTypes.func.isRequired,
  getInk: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  printers: state.printer.printers,
  loading: state.printer.loading,
});

export default connect(mapStateToProps, {
  getPrinters,
  getInk,
})(Printer);
